import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Hand, RefreshCw, MapPin, Check, User, ScanLine, Layers, Flag, ArrowRight, Repeat, AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import * as validationService from '@/services/validationService';
import { StaffPickerSelect } from '@/components/StaffPickerSelect';

const statusBadge = (s) => {
  const cfg = {
    generated: 'bg-blue-100 text-blue-700',
    assigned: 'bg-yellow-100 text-yellow-700',
    in_progress: 'bg-orange-100 text-orange-700',
    completed: 'bg-green-100 text-green-700',
  };
  return <Badge className={`${cfg[s] || cfg.generated} border-0`}>{(s || '').replace('_', ' ')}</Badge>;
};

// A same-design / different-shade slip (e.g. Lining White -> Lining · Cream) is a conversion too.
const hasPicks = (s) => (s?.picked_scans?.length || 0) > 0
  || (s?.lines || []).some((l) => l.consumed_bundles?.length);
const needsConversion = (s) => !!(s?.needs_conversion && !s?.converted
  && (s?.status !== 'completed' || hasPicks(s)) && (
  (s?.target_design_name && s.target_design_name !== s.design_name) || s?.target_shade));
const targetLabel = (s) => `${s?.target_design_name || ''}${s?.target_shade ? ` · ${s.target_shade}` : ''}`;

const AssortmentPicking = () => {
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [staff, setStaff] = useState([]);
  const [picker, setPicker] = useState('');
  const [scan, setScan] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null); // { color_name, pieces } of last accepted scan
  const scanRef = useRef(null);

  // ---- conversion review modal (dismissable — convert now or later) ----
  const [conv, setConv] = useState({ open: false, slip: null, loading: false, data: null, converting: false });

  const fetchSlips = async () => {
    setLoading(true);
    try {
      const d = await greigeStorageApi.getAssortmentPickSlips();
      const pickable = ['generated', 'assigned', 'in_progress'];
      setSlips((d.pick_slips || []).filter((s) =>
        pickable.includes(s.status) || (s.status === 'completed' && needsConversion(s))));
    } catch {
      toast.error('Failed to load pick slips');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try { const d = await validationService.getStaffList(null, null, 5000); setStaff(d.staff || []); }
    catch { toast.error('Failed to load staff list'); }
  };

  useEffect(() => { fetchSlips(); fetchStaff(); }, []);

  const openSlip = async (slip) => {
    // Completed slips only remain in the list when they still need conversion —
    // jump straight to the conversion review instead of the scan screen.
    if (slip.status === 'completed') { openConversion(slip); return; }
    try {
      const d = await greigeStorageApi.getAssortmentPickSlip(slip.pick_slip_id);
      setSelected(d.pick_slip);
      setLastScan(null);
      setTimeout(() => scanRef.current?.focus(), 150);
    } catch { toast.error('Failed to open slip'); }
  };

  const handleAssign = async () => {
    if (!picker) { toast.error('Select a picker'); return; }
    try {
      await greigeStorageApi.assignAssortmentPicker(selected.pick_slip_id, picker);
      toast.success('Picker assigned');
      const d = await greigeStorageApi.getAssortmentPickSlip(selected.pick_slip_id);
      setSelected(d.pick_slip);
      setTimeout(() => scanRef.current?.focus(), 150);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
  };

  const afterComplete = (res) => {
    // Picking finished. The backend now converts to the target design
    // AUTOMATICALLY. React to that result; only fall back to the manual
    // conversion dialog when auto-convert was skipped (unmapped sort nos).
    const ps = res?.pick_slip || res;
    const ac = res?.auto_conversion;
    setSelected(null);
    setLastScan(null);
    fetchSlips();
    if (ac?.converted) {
      toast.success(`Auto-converted ${ac.converted_count} bundle(s) → ${targetLabel(ps) || ac.target_design}`);
      return;
    }
    if (ac?.skipped === 'unmapped') {
      toast.warning(
        `Auto-conversion skipped — ${ac.unmapped_count} bundle(s) have no child sort no for the target design. Add them to the Greige Sort Nos master, then convert.`,
        { duration: 6000 });
      if (needsConversion(ps)) openConversion(ps);
      return;
    }
    // no different target design, or already converted → nothing to do
  };

  const handleScan = async (e) => {
    if (e.key !== 'Enter' || !scan.trim() || scanning) return;
    const code = scan.trim();
    setScanning(true);
    try {
      const res = await greigeStorageApi.scanAssortmentBundle(selected.pick_slip_id, code, selected.assigned_picker);
      setSelected(res.pick_slip);
      setLastScan({ color_name: res.scanned.color_name, pieces: res.scanned.pieces });
      setScan('');
      if (res.nudge) toast.warning(res.nudge, { duration: 4000 });
      toast.success(`+${res.scanned.pieces} ${res.qty_unit || res.scanned.qty_unit || 'pcs'} ${res.scanned.color_name}`);
      if (res.all_fulfilled) {
        toast.success('All colours fulfilled — slip completed!');
        afterComplete(res);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan rejected');
    } finally {
      setScan('');
      setScanning(false);
      setTimeout(() => scanRef.current?.focus(), 100);
    }
  };

  const handleFinish = async () => {
    try {
      const res = await greigeStorageApi.completeAssortmentSlip(selected.pick_slip_id);
      toast.success('Pick slip finished');
      afterComplete(res);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to finish'); }
  };

  const [poolSwitching, setPoolSwitching] = useState(false);
  const handleSetPool = async (pool) => {
    if (!selected || selected.pool === pool || poolSwitching) return;
    setPoolSwitching(true);
    try {
      const res = await greigeStorageApi.setAssortmentPool(selected.pick_slip_id, pool);
      setSelected(res.pick_slip);
      toast.success(res.message || `Switched to ${pool === 'nq' ? 'NQ' : 'Non-NQ'} bundles`);
      setTimeout(() => scanRef.current?.focus(), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch pool');
    } finally {
      setPoolSwitching(false);
    }
  };

  // ---- conversion modal ----
  const closeConv = () => setConv({ open: false, slip: null, loading: false, data: null, converting: false });

  const openConversion = async (slip) => {
    setConv({ open: true, slip, loading: true, data: null, converting: false });
    try {
      const data = await greigeStorageApi.getAssortmentConversionPreview(slip.pick_slip_id);
      setConv((p) => (p.slip?.pick_slip_id === slip.pick_slip_id ? { ...p, loading: false, data } : p));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load conversion preview');
      closeConv();
    }
  };

  const runConversion = async () => {
    if (!conv.slip) return;
    setConv((p) => ({ ...p, converting: true }));
    try {
      const res = await greigeStorageApi.convertAssortmentSlip(conv.slip.pick_slip_id, conv.slip.assigned_picker || null);
      toast.success(`Converted ${res.converted_count} bundle(s) → ${targetLabel(conv.slip) || res.target_design}`);
      closeConv();
      setSelected(null);
      fetchSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
      setConv((p) => ({ ...p, converting: false }));
    }
  };

  const colorTargets = selected?.color_targets || [];
  const unit = selected?.qty_unit || 'pcs';
  const totalTarget = colorTargets.reduce((a, c) => a + (c.target_qty || 0), 0);
  const totalPicked = colorTargets.reduce((a, c) => a + (c.picked_pieces || 0), 0);
  const progress = totalTarget ? Math.min(100, (totalPicked / totalTarget) * 100) : 0;
  const locationsFor = (color) => (selected?.lines || []).filter((l) => l.color_name === color);
  const pickedBundlesFor = (color) => (selected?.picked_scans || []).filter((s) => s.color_name === color).length;

  const convData = conv.data;
  const convItems = convData?.items || [];
  const convUnmapped = convData?.unmapped || [];
  const convChange = convData?.change_count || 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 min-h-16 pt-12 pb-3 sm:pt-0 sm:pb-0 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => selected ? setSelected(null) : navigate('/sections/greige-storage')} className="text-[#F9FAFB] hover:bg-white/10 shrink-0" data-testid="assortment-picking-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <Hand className="w-6 h-6 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold font-display truncate">Assortment Picking</h1>
                <p className="text-xs sm:text-sm text-[#E8DCC8] truncate">Scan each bundle as you pull it</p>
              </div>
            </div>
          </div>
          {!selected && (
            <Button variant="outline" size="icon" onClick={fetchSlips} className="bg-white/10 text-white border-white/20 hover:bg-white/20 shrink-0" data-testid="assortment-picking-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 sm:py-8">
        {!selected ? (
          loading ? (
            <div className="py-16 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#2D5F4F]" /></div>
          ) : slips.length === 0 ? (
            <Card className="border-[#E5DFD0]"><CardContent className="py-16 text-center">
              <Layers className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="font-semibold text-[#1A1F2E] mb-1">No active assortment pick slips</h3>
              <Button className="mt-3 h-11 bg-[#2D5F4F] hover:bg-[#1A1F2E]" onClick={() => navigate('/sections/greige-storage/assortment-pick-slip')}>Create one</Button>
            </CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {slips.map((s) => {
                const convNeeded = needsConversion(s);
                const isCompleted = s.status === 'completed';
                return (
                  <Card key={s.pick_slip_id} className="border-[#E5DFD0] hover:border-[#2D5F4F] cursor-pointer transition-colors active:scale-[0.99]" onClick={() => openSlip(s)} data-testid={`assortment-picking-slip-${s.pick_slip_id}`}>
                    <CardContent className="py-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1A1F2E] flex items-center gap-1.5 flex-wrap">
                          <span className="truncate">{s.design_name}</span>
                          {convNeeded && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] font-medium bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5" data-testid={`assortment-picking-target-${s.pick_slip_id}`}>
                              <ArrowRight className="w-3 h-3" /> {targetLabel(s)}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-[#5A5F6E]">{s.allocated_qty} {s.qty_unit || 'pcs'} · {s.color_targets?.length} {s.color_targets?.length === 1 ? 'colour' : 'colours'} · {s.pool === 'nq' ? 'NQ' : 'Non-NQ'}{s.low_colors ? ' · <6 colours' : ''}</p>
                      </div>
                      <div className="text-right space-y-1 shrink-0">
                        {isCompleted && convNeeded ? (
                          <Button size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700" onClick={(e) => { e.stopPropagation(); openConversion(s); }} data-testid={`assortment-convert-btn-${s.pick_slip_id}`}>
                            <Repeat className="w-3.5 h-3.5 mr-1" /> Convert
                          </Button>
                        ) : (
                          <>
                            {statusBadge(s.status)}
                            <p className="text-xs text-[#5A5F6E]">{s.assigned_picker_name ? `Picker: ${s.assigned_picker_name}` : 'Unassigned'}</p>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        ) : (
          <>
            <Card className="border-[#E5DFD0] mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-2 text-base">
                  <span className="min-w-0 truncate">{selected.design_name}</span>
                  {statusBadge(selected.status)}
                </CardTitle>
                {needsConversion(selected) && (
                  <p className="text-xs text-indigo-700 flex items-center gap-1 pt-1" data-testid="assortment-picking-detail-target">
                    <ArrowRight className="w-3.5 h-3.5" /> Converts to <b>{targetLabel(selected)}</b> on finish
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div><p className="text-xs text-[#5A5F6E]">Target</p><p className="font-medium">{totalTarget} {unit}</p></div>
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Pool</p>
                    <div className="inline-flex rounded-md border border-[#E5DFD0] overflow-hidden mt-0.5" data-testid="assortment-pool-toggle">
                      {['non_nq', 'nq'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          disabled={poolSwitching || totalPicked > 0}
                          onClick={() => handleSetPool(p)}
                          className={`px-3 py-1 text-xs font-semibold transition-colors ${selected.pool === p ? 'bg-[#2D5F4F] text-white' : 'bg-white text-[#5A5F6E] hover:bg-gray-50'} ${(poolSwitching || totalPicked > 0) && selected.pool !== p ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`assortment-pool-${p}`}
                        >
                          {p === 'nq' ? 'NQ' : 'Non-NQ'}
                        </button>
                      ))}
                    </div>
                    {totalPicked > 0 && (
                      <p className="text-[10px] text-amber-600 mt-0.5" data-testid="assortment-pool-locked-note">Locked — picking started</p>
                    )}
                  </div>
                  <div><p className="text-xs text-[#5A5F6E]">Picked</p><p className="font-medium" data-testid="assortment-picking-progress">{totalPicked} / {totalTarget} {unit}</p></div>
                </div>
                <Progress value={progress} className="h-2" />
                {selected.approval && (
                  <p className="text-xs text-amber-700 mt-2 flex items-center gap-1"><Flag className="w-3 h-3" /> {selected.low_colors ? `<6-colour approval by ${selected.approval.approved_by_name}: ${selected.approval.reason}` : `Approved by ${selected.approval.approved_by_name}`}</p>
                )}
              </CardContent>
            </Card>

            {!selected.assigned_picker ? (
              <Card className="border-[#E5DFD0] mb-4">
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><User className="w-5 h-5" /> Assign Picker</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <StaffPickerSelect staff={staff} value={picker} onChange={setPicker}
                      className="flex-1 h-12" testId="assortment-assign-picker" />
                    <Button onClick={handleAssign} className="h-12 bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="assortment-assign-btn">Assign</Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3 p-3 bg-green-50 rounded-lg border border-green-200 text-green-700 text-sm">
                  <User className="w-4 h-4 shrink-0" /> Picker: {selected.assigned_picker_name}
                </div>
                {/* Last-scanned colour banner — tells the picker whether to keep
                    scanning this colour or move on. */}
                {lastScan && (() => {
                  const ct = (selected.color_targets || []).find((c) => c.color_name === lastScan.color_name) || {};
                  const picked = ct.picked_pieces || 0;
                  const target = ct.target_qty || 0;
                  const remaining = Math.max(0, target - picked);
                  const done = target > 0 && picked >= target;
                  const near = !done && target > 0 && picked >= target * 0.85;
                  const tone = done
                    ? { box: 'border-green-400 bg-green-50', dot: 'bg-green-500', text: 'text-green-800', pill: 'bg-green-600' }
                    : near
                      ? { box: 'border-amber-400 bg-amber-50', dot: 'bg-amber-500', text: 'text-amber-800', pill: 'bg-amber-500' }
                      : { box: 'border-[#2D5F4F] bg-[#2D5F4F]/5', dot: 'bg-[#2D5F4F]', text: 'text-[#1A1F2E]', pill: 'bg-[#2D5F4F]' };
                  const guidance = done
                    ? `Complete — do NOT scan another ${lastScan.color_name} bundle`
                    : near
                      ? `Almost done — only ${remaining} ${unit} left. Don't scan another ${lastScan.color_name} bundle`
                      : `Keep scanning ${lastScan.color_name} — ${remaining} ${unit} still needed`;
                  return (
                    <div className={`mb-4 rounded-xl border-2 ${tone.box} p-4`} data-testid="assortment-last-scan-banner">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <span className={`w-3.5 h-3.5 rounded-full ${tone.dot} shrink-0`} />
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-[#5A5F6E] font-medium">Last scanned colour</div>
                            <div className={`text-2xl font-extrabold leading-tight ${tone.text}`} data-testid="assortment-last-scan-color">{lastScan.color_name}</div>
                          </div>
                          <Badge className={`${tone.pill} text-white border-0 ml-1`} data-testid="assortment-last-scan-added">+{lastScan.pieces} {unit}</Badge>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-[#1A1F2E]" data-testid="assortment-last-scan-progress">{picked} / {target}</div>
                          <div className="text-[11px] text-[#5A5F6E]">picked</div>
                        </div>
                      </div>
                      <p className={`mt-2 text-sm font-semibold ${tone.text}`} data-testid="assortment-last-scan-guidance">
                        {done ? '✓ ' : near ? '⚠ ' : '→ '}{guidance}
                      </p>
                    </div>
                  );
                })()}
                {/* Scan box */}
                <Card className="border-[#2D5F4F] border-2 mb-4">
                  <CardContent className="py-4">
                    <label className="text-sm font-medium text-[#1A1F2E] flex items-center gap-2 mb-2"><ScanLine className="w-4 h-4" /> Scan each bundle as you pull it</label>
                    <Input
                      ref={scanRef}
                      value={scan}
                      onChange={(e) => setScan(e.target.value)}
                      onKeyDown={handleScan}
                      disabled={scanning}
                      placeholder="Scan bundle barcode + Enter..."
                      className="h-14 text-lg"
                      data-testid="assortment-scan-input"
                    />
                    <p className="text-xs text-[#5A5F6E] mt-2">Any bundle of this design & a needed colour works — no specific code required.</p>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Per-colour tally */}
            <div className="grid sm:grid-cols-2 gap-3">
              {colorTargets.map((c) => {
                const pct = c.target_qty ? Math.min(100, ((c.picked_pieces || 0) / c.target_qty) * 100) : 0;
                const done = (c.picked_pieces || 0) >= (c.target_qty || 0);
                // Locked for NEW bundles once within 15% of target (>= 85% picked)
                // but not yet fully done — mirrors the backend scan guard.
                const locked = !done && c.target_qty > 0 && (c.picked_pieces || 0) >= c.target_qty * 0.85;
                const remaining = Math.max(0, (c.target_qty || 0) - (c.picked_pieces || 0));
                const bundlesPicked = pickedBundlesFor(c.color_name);
                return (
                  <Card key={c.color_name} className={`border ${done ? 'border-green-300 bg-green-50' : locked ? 'border-amber-300 bg-amber-50' : 'border-[#E5DFD0] bg-white'}`} data-testid={`assortment-tally-${c.color_name}`}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {done && <Check className="w-4 h-4 text-green-600" />}
                          {locked && <Lock className="w-4 h-4 text-amber-600" data-testid={`assortment-locked-${c.color_name}`} />}
                          <Badge className="bg-[#2D5F4F]/10 text-[#2D5F4F] border-0">{c.color_name}</Badge>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-[#1A1F2E] block" data-testid={`assortment-tally-count-${c.color_name}`}>{c.picked_pieces || 0} / {c.target_qty} {unit}</span>
                          <span className="text-[11px] font-medium text-[#5A5F6E]" data-testid={`assortment-tally-meta-${c.color_name}`}>{bundlesPicked} bundle{bundlesPicked === 1 ? '' : 's'} picked · {remaining} {unit} left</span>
                        </div>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                      {locked && (
                        <p className="mt-1.5 text-[11px] font-medium text-amber-700" data-testid={`assortment-locked-msg-${c.color_name}`}>
                          Near target — new bundles blocked ({remaining} {unit} left). Finish slip to accept.
                        </p>
                      )}
                      <div className="mt-2 space-y-1" data-testid={`assortment-locations-${c.color_name}`}>
                        {locationsFor(c.color_name).map((l) => (
                          <div key={l.line_id} className="text-[11px] text-[#1A1F2E] flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1 border" data-testid={`assortment-loc-${l.line_id}`}>
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <MapPin className="w-3 h-3 shrink-0 text-[#2D5F4F]" />
                              <span className="truncate">{l.footprint || l.location_id}</span>
                              <span className="text-[#5A5F6E]">· {(l.candidate_bundles || []).length} bundle(s)</span>
                            </span>
                            <span className="shrink-0 font-semibold text-[#2D5F4F]">pull {l.target_pieces} {unit}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {selected.assigned_picker && (
              <Button variant="outline" onClick={handleFinish} className="w-full mt-4 h-auto min-h-12 py-2.5 whitespace-normal border-[#2D5F4F] text-[#2D5F4F]" data-testid="assortment-finish-btn">
                <Check className="w-4 h-4 mr-2 shrink-0" />
                <span className="sm:hidden">Finish slip</span>
                <span className="hidden sm:inline">Finish slip (face empty / accept minor difference)</span>
              </Button>
            )}
          </>
        )}
      </main>

      {/* ---------------- Conversion Review modal ---------------- */}
      <Dialog open={conv.open} onOpenChange={(v) => { if (!v && !conv.converting) closeConv(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="conversion-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-indigo-600" />
              Convert to {conv.slip ? targetLabel(conv.slip) : (convData?.target_design || '—')}
            </DialogTitle>
            <DialogDescription>
              Review each picked bundle's sort-number transfer. This design-transfers the greige to the target SKU. The internal transfer to Processing stays a separate manual step.
            </DialogDescription>
          </DialogHeader>

          {conv.loading ? (
            <div className="py-12 text-center"><RefreshCw className="w-7 h-7 animate-spin mx-auto text-indigo-600" /></div>
          ) : convData ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge className="bg-gray-100 text-gray-700 border-0">{convData.source_design}</Badge>
                <ArrowRight className="w-3.5 h-3.5 text-[#5A5F6E]" />
                <Badge className="bg-indigo-100 text-indigo-700 border-0">{targetLabel(conv.slip) || convData.target_design}</Badge>
                <span className="text-[#5A5F6E]">· <b className="text-[#1A1F2E]">{convChange}</b> to transfer · {convItems.length - convChange} already correct</span>
              </div>

              {convUnmapped.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1.5" data-testid="conversion-unmapped-block">
                  <p className="text-xs text-red-700 font-medium flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Cannot convert — {convUnmapped.length} picked bundle(s) have no child sort number for "{targetLabel(conv.slip) || convData.target_design}". Add these sort numbers to the Greige Sort Nos master:
                  </p>
                  {convUnmapped.map((u) => (
                    <div key={u.bundle_barcode} className="text-[11px] text-red-700 bg-white/60 rounded px-2 py-1 border border-red-100" data-testid={`conversion-unmapped-${u.bundle_barcode}`}>
                      <b>{u.bundle_barcode}</b> · sort {u.current_sort_no || '—'} · {u.reason}
                    </div>
                  ))}
                </div>
              )}

              {convItems.length > 0 && (
                <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1" data-testid="conversion-items">
                  {convItems.map((it) => (
                    <div key={it.bundle_barcode} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-[#E5DFD0] bg-white px-2.5 py-2" data-testid={`conversion-item-${it.bundle_barcode}`}>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[#1A1F2E] break-all">{it.bundle_barcode}</p>
                        {it.color_name && <p className="text-[11px] text-[#5A5F6E]">{it.color_name}</p>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap sm:shrink-0 text-[11px]">
                        <span className="text-[#5A5F6E]">sort {it.current_sort_no}</span>
                        <ArrowRight className="w-3 h-3 text-[#2D5F4F]" />
                        <span className="font-semibold text-[#2D5F4F]">sort {it.child_sort_no}</span>
                        {it.needs_change
                          ? <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">transfer</Badge>
                          : <Badge className="bg-gray-100 text-gray-500 border-0 text-[10px]">no change</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {convItems.length === 0 && convUnmapped.length === 0 && (
                <p className="text-sm text-[#5A5F6E] py-4 text-center">No picked bundles found to convert.</p>
              )}
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={closeConv} disabled={conv.converting} className="h-11" data-testid="conversion-later-btn">
              Convert Later
            </Button>
            <Button
              onClick={runConversion}
              disabled={conv.converting || conv.loading || !convData?.all_mapped}
              className="h-11 bg-indigo-600 hover:bg-indigo-700"
              data-testid="conversion-confirm-btn"
            >
              <Repeat className={`w-4 h-4 mr-2 ${conv.converting ? 'animate-spin' : ''}`} />
              {conv.converting ? 'Converting…' : `Convert Now (${convChange})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssortmentPicking;
